import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchReservationDetail } from '@/apis/aipass'
import { CustomerTagInput } from '@/components/molecules/customer/customer-tag-input'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { reloadAll } from '@/features/restaurant-manager/restaurant-manager-thunks'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { RestaurantCustomerTag } from '@/models/restaurant-manager'
import { RestaurantEditableCellShell } from './restaurant-editable-cell-shell'
import { useRestaurantInlineEditor } from './use-restaurant-inline-editor'

type Props = {
  customerTags: RestaurantCustomerTag[]
  customerId: string | null
  reservationId: string
}

export const RestaurantCustomerTagsCell: React.FC<Props> = ({ customerTags, customerId, reservationId }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const dispatch = useAppDispatch()
  const query = useAppSelector(state => state.restaurantManager.query)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isTagMutating, setIsTagMutating] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedCustomerTagMasterIds, setSelectedCustomerTagMasterIds] = useState<string[]>([])
  const hasMutatedRef = useRef(false)

  const closeEditor = useCallback(async () => {
    if (isLoading || isTagMutating) {
      return
    }

    setIsEditing(false)
    setIsDropdownOpen(false)
    if (hasMutatedRef.current) {
      await dispatch(reloadAll(query))
    }
    hasMutatedRef.current = false
  }, [dispatch, isLoading, isTagMutating, query])

  const { containerRef, handleCompositionEnd, handleCompositionStart, handleEditorKeyDown } = useRestaurantInlineEditor({
    isEditing,
    isBusy: isLoading || isTagMutating,
    onRequestClose: closeEditor,
    canCloseOnEnter: () => !isDropdownOpen,
  })

  const loadSelectedTags = useCallback(async () => {
    if (!customerId) {
      return false
    }

    try {
      setIsLoading(true)
      const detail = await fetchReservationDetail({ reservationId })
      const resolvedCustomerId = detail?.customer?.id

      if (!resolvedCustomerId || resolvedCustomerId !== customerId) {
        throw new Error('Customer tag target could not be resolved.')
      }

      setSelectedCustomerTagMasterIds(detail?.customer?.tagInfo?.tags?.map(tag => tag.id) ?? [])
      return true
    } catch (error) {
      errorHandler(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [customerId, errorHandler, reservationId])

  const startEdit = useCallback(async () => {
    if (!customerId || isLoading || isTagMutating) {
      return
    }

    hasMutatedRef.current = false
    setIsEditing(true)
    const loaded = await loadSelectedTags()
    if (!loaded) {
      setIsEditing(false)
    }
  }, [customerId, isLoading, isTagMutating, loadSelectedTags])

  const handleTagBusyChange = useCallback((busy: boolean) => {
    setIsTagMutating(prev => {
      // true → false の遷移時（mutation 完了時）のみ dirty フラグを立てる
      if (prev && !busy) {
        hasMutatedRef.current = true
      }
      return busy
    })
  }, [])

  useEffect(() => {
    if (!isEditing || isLoading) {
      return
    }

    const input = containerRef.current?.querySelector('input')
    if (input instanceof HTMLInputElement) {
      input.focus()
    }
  }, [containerRef, isEditing, isLoading])

  const createCustomerTagStyle = useCallback(
    (backgroundColor: string, fontColor: string, borderColor: string) =>
      css({
        background: backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 12,
        color: fontColor,
        display: 'inline-flex',
        fontSize: 10,
        fontWeight: 'bold',
        lineHeight: 1.2,
        padding: '4px 8px',
        whiteSpace: 'nowrap',
      }),
    [],
  )

  const displayContent = useMemo(() => {
    if (customerTags.length === 0) {
      return <span css={customerTagPlaceholderStyle}>-</span>
    }

    return customerTags.map(tag => (
      <span
        key={`${tag.name}-${tag.backgroundColor}-${tag.borderColor}`}
        css={createCustomerTagStyle(tag.backgroundColor, tag.fontColor, tag.borderColor)}
      >
        {tag.name}
      </span>
    ))
  }, [customerTags])

  return (
    <div
      ref={containerRef}
      css={customerTagsCellStyle}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={isEditing ? handleEditorKeyDown : undefined}
    >
      <RestaurantEditableCellShell
        canEdit={customerId != null}
        isEditing={isEditing}
        onStartEdit={() => void startEdit()}
        openOnClick={customerId != null}
        editButtonAriaLabel={t('Edit')}
      >
        {isEditing && customerId ? (
          <div css={editorFrameStyle}>
            {isLoading ? (
              <div css={loadingTextStyle}>{t('Loading')}</div>
            ) : (
              <CustomerTagInput
                customerId={customerId}
                customerTags={selectedCustomerTagMasterIds}
                getPopupContainer={() => containerRef.current ?? document.body}
                dropdownStyle={{ minWidth: 200 }}
                onDropdownVisibleChange={setIsDropdownOpen}
                onBusyChange={handleTagBusyChange}
                customStyle={inlineTagInputStyle}
              />
            )}
          </div>
        ) : (
          <div css={customerTagsStyle}>{displayContent}</div>
        )}
      </RestaurantEditableCellShell>
    </div>
  )
}

const customerTagsStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  minWidth: 0,
  width: '100%',
})

const customerTagsCellStyle = css({
  minWidth: 0,
  width: '100%',
})

const editorFrameStyle = css({
  background: '#FFFFFF',
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  minHeight: 152,
  padding: 12,
  width: '100%',
})

const inlineTagInputStyle = css({
  height: '100%',
  '.tag-select': {
    height: '100%',
    '> div': {
      borderRadius: 0,
      minHeight: 'auto',
      padding: 0,
    },
  },
  '.ant-select-selection--multiple': {
    background: '#FFFFFF !important',
    border: 'none !important',
    minHeight: 'auto',
  },
})

const loadingTextStyle = css({
  color: '#676767',
  fontSize: 12,
  minHeight: 126,
})

const customerTagPlaceholderStyle = css({
  color: '#272727',
})
